import React from "react";
import { motion } from "framer-motion";
import { Brain, CircleNotch, X, TrendUp, TrendDown, Minus, ArrowRight, Warning, PushPin, PushPinSlash, ArrowsOut, ArrowsIn } from "@phosphor-icons/react";

/**
 * ChartAnalysisPanel — slide-down panel that renders the structured AI
 * technical analysis returned by /api/chart/analyze. Sits directly below
 * the chart header inside StockDetailModal. Closing it via the parent
 * also clears the S/R overlay lines on the chart.
 */
const TREND_ICON = { Up: TrendUp, Down: TrendDown, Sideways: Minus };
const STRENGTH_TONE = {
  Strong: "text-pos border-pos/60 bg-pos-soft/40",
  Moderate: "text-mod border-mod/60 bg-mod-soft/40",
  Weak: "text-muted-fg border-subtle bg-surface",
};
const BIAS_TONE = {
  Bullish: "text-pos border-pos/60 bg-pos-soft/40",
  Bearish: "text-neg border-neg/60 bg-neg-soft/40",
  Neutral: "text-secondary-fg border-subtle bg-surface",
};

function TrendChip({ label, value }) {
  const Icon = TREND_ICON[value] || Minus;
  const tone =
    value === "Up" ? "text-pos" : value === "Down" ? "text-neg" : "text-muted-fg";
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <span className="font-mono uppercase tracking-widest text-muted-fg">{label}</span>
      <Icon size={12} weight="bold" className={tone} />
      <span className={"font-medium " + tone}>{value}</span>
    </div>
  );
}

export default function ChartAnalysisPanel({ data, loading, error, onClose, onRefresh, onSaveToWatchlist, onTogglePin, pinnedKeys, watched, generatedAt, cached, expanded, onToggleExpand }) {
  const [saveState, setSaveState] = React.useState("idle"); // idle | saving | saved | error
  if (!data && !loading && !error) return null;

  /** Pin key — `${kind}|${roundedPrice}` so the parent can know which
   * rows are already pinned without us caring about the IDs. */
  const pinKey = (kind, price) => `${kind}|${Number(price).toFixed(2)}`;

  const handleSave = async () => {
    if (!onSaveToWatchlist || !data) return;
    setSaveState("saving");
    try {
      await onSaveToWatchlist();
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 3000);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="border border-pos/40 bg-pos-soft/10 rounded-sm"
      data-testid="chart-analysis-panel"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-subtle bg-surface/60">
        <div className="flex items-center gap-2">
          <Brain size={14} weight="fill" className="text-pos" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary-fg">
            AI Chart Analysis
          </span>
          {cached && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-muted-fg border border-subtle rounded-sm px-1.5">
              Cached
            </span>
          )}
          {generatedAt && (
            <span className="text-[10px] text-muted-fg">
              · {new Date(generatedAt).toLocaleString("en-IN")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {onRefresh && !loading && (
            <button
              type="button"
              onClick={onRefresh}
              data-testid="chart-analysis-refresh"
              className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-subtle rounded-sm text-secondary-fg hover:border-pos/60 hover:text-pos"
            >
              Refresh
            </button>
          )}
          {onToggleExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              data-testid="chart-analysis-expand"
              title={expanded ? "Collapse panel" : "Expand to wide view"}
              className="hidden xl:inline-flex p-1 rounded-sm text-muted-fg hover:text-pos transition"
            >
              {expanded ? <ArrowsIn size={13} weight="bold" /> : <ArrowsOut size={13} weight="bold" />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="chart-analysis-close"
            className="p-1 rounded-sm text-muted-fg hover:text-primary-fg"
            title="Close analysis · clears S/R lines from chart"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-6 text-secondary-fg">
            <CircleNotch size={16} className="animate-spin" />
            <span className="text-xs font-mono uppercase tracking-widest">
              Gemini analysing chart…
            </span>
          </div>
        )}
        {error && (
          <div className="text-xs text-neg border border-neg/40 bg-neg-soft/30 rounded-sm p-2.5">
            {error}
          </div>
        )}
        {data && !loading && (
          <>
            {/* Trend row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <TrendChip label="Short" value={data.trend?.short} />
              <TrendChip label="Medium" value={data.trend?.medium} />
              <TrendChip label="Long" value={data.trend?.long} />
              <span
                className={
                  "ml-auto px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border rounded-sm " +
                  (BIAS_TONE[data.bias] || BIAS_TONE.Neutral)
                }
                data-testid="chart-analysis-bias"
              >
                Bias: {data.bias || "Neutral"}
              </span>
            </div>

            {/* Momentum + Volume strip — single column normally,
                2-column when the panel is expanded. */}
            <div className={"grid gap-2 text-[11px] " + (expanded ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1")}>
              <div className="border border-subtle rounded-sm p-2 bg-surface/60">
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg mb-1">
                  Momentum
                </div>
                <div className="flex items-center gap-2 text-secondary-fg">
                  <span className="font-medium text-primary-fg">RSI:</span> {data.momentum?.rsi_state}
                  <span className="text-muted-fg">·</span>
                  <span className="font-medium text-primary-fg">MACD:</span> {data.momentum?.macd_state}
                </div>
                {data.momentum?.note && (
                  <div className="text-[10px] text-muted-fg mt-0.5">{data.momentum.note}</div>
                )}
              </div>
              <div className="border border-subtle rounded-sm p-2 bg-surface/60">
                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg mb-1">
                  Volume
                </div>
                <div className="text-secondary-fg">
                  <span className="font-medium text-primary-fg">{data.volume?.state}</span>
                </div>
                {data.volume?.note && (
                  <div className="text-[10px] text-muted-fg mt-0.5">{data.volume.note}</div>
                )}
              </div>
            </div>

            {/* Support / Resistance lists — single column inside the
                narrow (~400px) side-panel; switches to 2-column when
                expanded for extra breathing room. */}
            <div className={"grid gap-2 " + (expanded ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1")}>
              <div className="border border-pos/30 rounded-sm p-2 bg-pos-soft/10">
                <div className="text-[9px] font-mono uppercase tracking-widest text-pos mb-1.5">
                  Strong Supports
                </div>
                <div className="space-y-1">
                  {(data.supports || []).map((s, i) => {
                    const isPinned = !!pinnedKeys?.has?.(pinKey("support", s.price));
                    return (
                      <div key={`sup-${i}`} className="text-[11px] py-1 border-b border-pos/15 last:border-0" data-testid={`chart-analysis-support-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono tabular-nums text-primary-fg">₹{s.price?.toLocaleString("en-IN")}</span>
                          <span
                            className={
                              "px-1 py-0 text-[8px] font-mono uppercase tracking-widest border rounded-sm shrink-0 " +
                              (STRENGTH_TONE[s.strength] || STRENGTH_TONE.Weak)
                            }
                          >
                            {s.strength}
                          </span>
                          {onTogglePin && (
                            <button
                              type="button"
                              onClick={() => onTogglePin({ ...s, kind: "support" })}
                              data-testid={`chart-analysis-pin-support-${i}`}
                              title={isPinned ? "Unpin — won't show on chart by default" : "Pin to chart — stays after closing the analysis"}
                              className={
                                "ml-auto shrink-0 p-0.5 rounded-sm transition " +
                                (isPinned ? "text-pos" : "text-muted-fg hover:text-pos")
                              }
                            >
                              {isPinned ? <PushPin size={12} weight="fill" /> : <PushPinSlash size={12} />}
                            </button>
                          )}
                        </div>
                        {s.why && (
                          <div className="text-[10px] text-muted-fg leading-snug mt-0.5">{s.why}</div>
                        )}
                      </div>
                    );
                  })}
                  {(!data.supports || data.supports.length === 0) && (
                    <div className="text-[11px] text-muted-fg italic">No strong supports identified.</div>
                  )}
                </div>
              </div>
              <div className="border border-neg/30 rounded-sm p-2 bg-neg-soft/10">
                <div className="text-[9px] font-mono uppercase tracking-widest text-neg mb-1.5">
                  Strong Resistances
                </div>
                <div className="space-y-1">
                  {(data.resistances || []).map((r, i) => {
                    const isPinned = !!pinnedKeys?.has?.(pinKey("resistance", r.price));
                    return (
                      <div key={`res-${i}`} className="text-[11px] py-1 border-b border-neg/15 last:border-0" data-testid={`chart-analysis-resistance-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono tabular-nums text-primary-fg">₹{r.price?.toLocaleString("en-IN")}</span>
                          <span
                            className={
                              "px-1 py-0 text-[8px] font-mono uppercase tracking-widest border rounded-sm shrink-0 " +
                              (STRENGTH_TONE[r.strength] || STRENGTH_TONE.Weak)
                            }
                          >
                            {r.strength}
                          </span>
                          {onTogglePin && (
                            <button
                              type="button"
                              onClick={() => onTogglePin({ ...r, kind: "resistance" })}
                              data-testid={`chart-analysis-pin-resistance-${i}`}
                              title={isPinned ? "Unpin — won't show on chart by default" : "Pin to chart — stays after closing the analysis"}
                              className={
                                "ml-auto shrink-0 p-0.5 rounded-sm transition " +
                                (isPinned ? "text-neg" : "text-muted-fg hover:text-neg")
                              }
                            >
                              {isPinned ? <PushPin size={12} weight="fill" /> : <PushPinSlash size={12} />}
                            </button>
                          )}
                        </div>
                        {r.why && (
                          <div className="text-[10px] text-muted-fg leading-snug mt-0.5">{r.why}</div>
                        )}
                      </div>
                    );
                  })}
                  {(!data.resistances || data.resistances.length === 0) && (
                    <div className="text-[11px] text-muted-fg italic">No strong resistances identified.</div>
                  )}
                </div>
              </div>
            </div>

            {/* Entry zone */}
            {data.entry_zone && (data.entry_zone.low || data.entry_zone.high) && (
              <div className="border border-subtle rounded-sm p-2 bg-surface/60 flex items-start gap-2 text-[11px]">
                <ArrowRight size={12} weight="bold" className="text-pos shrink-0 mt-0.5" />
                <div>
                  <span className="font-mono uppercase tracking-widest text-[9px] text-muted-fg mr-1.5">Watch zone</span>
                  <span className="font-mono tabular-nums text-primary-fg">
                    ₹{data.entry_zone.low?.toLocaleString("en-IN")} – ₹{data.entry_zone.high?.toLocaleString("en-IN")}
                  </span>
                  {data.entry_zone.note && (
                    <span className="text-muted-fg ml-1.5">· {data.entry_zone.note}</span>
                  )}
                </div>
              </div>
            )}

            {/* Cautions */}
            {Array.isArray(data.cautions) && data.cautions.length > 0 && (
              <div className="border border-mod/30 rounded-sm p-2 bg-mod-soft/15">
                <div className="text-[9px] font-mono uppercase tracking-widest text-mod mb-1 flex items-center gap-1">
                  <Warning size={10} weight="fill" />
                  Cautions
                </div>
                <ul className="text-[11px] text-secondary-fg list-disc ml-4 space-y-0.5">
                  {data.cautions.map((c, i) => (
                    <li key={`c-${i}`}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Summary */}
            {data.summary && (
              <div
                className="text-[11px] text-secondary-fg leading-relaxed border-l-2 border-pos/40 pl-2.5"
                data-testid="chart-analysis-summary"
              >
                {data.summary}
              </div>
            )}

            {/* Save to watchlist with AI note */}
            {onSaveToWatchlist && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-[10px] text-muted-fg">
                  {watched ? "Already in watchlist — clicking will update the note." : "Pin this AI reading to your watchlist."}
                </span>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saveState === "saving" || saveState === "saved"}
                  data-testid="chart-analysis-save-watchlist"
                  className={
                    "px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border rounded-sm transition flex items-center gap-1.5 disabled:opacity-60 " +
                    (saveState === "saved"
                      ? "border-pos bg-pos text-black"
                      : saveState === "error"
                      ? "border-neg text-neg"
                      : "border-pos/60 text-pos hover:bg-pos-soft/40")
                  }
                >
                  {saveState === "saving" && <CircleNotch size={11} className="animate-spin" />}
                  {saveState === "saved"
                    ? "★ Saved"
                    : saveState === "error"
                    ? "Failed — retry"
                    : watched
                    ? "Update Watchlist Note"
                    : "Save to Watchlist + AI Note"}
                </button>
              </div>
            )}

            <div className="text-[9px] text-muted-fg/70 italic">
              AI-generated technical reading. Educational use only · not investment advice.
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
